import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, Inject, OnInit, Renderer2 } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { ResourceApi } from 'src/app/shared/apis/resource.api';
import { LangModel } from 'src/app/shared/interfaces/country.interface';
import { FooterList, LicensePic } from 'src/app/shared/interfaces/resource.interface';
import { CountryUtilsService } from 'src/app/shared/service/country.service';
import { LayoutService } from 'src/app/shared/service/layout.service';
import { LocaleService } from 'src/app/shared/service/locale.service';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { ToastService } from 'src/app/shared/service/toast.service';

// 擴展 window 對象類型
declare global {
  interface Window {
    [key: string]: any;
  }
}
@UntilDestroy()
@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent implements OnInit, AfterViewInit {
  page!: string;
  isH5!: boolean;
  simple!: boolean;
  disclaimer!: string;
  licensePic!: LicensePic[];
  currentConfig: any;

  // 定義兩個網域的配置
  private configs = {
    'jackpot88.io': {
      id: '01e82e35-efaf-43f6-84eb-d8dfa958cdb2',
      initFn: 'anj_01e82e35_efaf_43f6_84eb_d8dfa958cdb2',
    },
    'jp88.io': {
      id: '166fc5cd-0625-4899-9b42-916fc8daf648',
      initFn: 'anj_166fc5cd_0625_4899_9b42_916fc8daf648',
    },
  };

  aboutUSList: FooterList = { type: 'AboutUs', name: this.localeService.getValue('abount_us'), detail: [] };
  productList: FooterList = { type: 'Product', name: this.localeService.getValue('product_title'), detail: [] };
  studyList: FooterList = { type: 'Study', name: this.localeService.getValue('study_title'), detail: [] };
  gamblingList: FooterList = {
    type: 'GamblingAddiction',
    name: this.localeService.getValue('gambling_addiction_title'),
    detail: [],
  };
  helpList: FooterList = { type: 'Help', name: this.localeService.getValue('help_title'), detail: [] };
  communityList: FooterList = { type: 'Community', name: 'Community', detail: [] };
  footerList = [
    this.aboutUSList,
    this.productList,
    this.helpList,
    this.studyList,
    this.gamblingList,
    this.communityList,
  ];
  h5SelectedIndex?: number;

  constructor(
    public layout: LayoutService,
    private appService: AppService,
    private countryUtils: CountryUtilsService,
    private router: Router,
    private resourceApi: ResourceApi,
    private toast: ToastService,
    private localeService: LocaleService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private localStorageService: LocalStorageService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.layout.isH5$.pipe(untilDestroyed(this)).subscribe(e => (this.isH5 = e));
    this.layout.page$.pipe(untilDestroyed(this)).subscribe(e => {
      this.page = e;
      this.simple = ['register', 'login', 'password'].includes(e);
    });
  }

  countries!: LangModel[]; //lang全部信息
  /** 当前选中的语言 */
  selectedLang: LangModel | undefined;
  /** Footer数据缓存 */
  static footerCacheData: any;
  currentYear: string = '';

  async ngOnInit() {
    this.currentYear = new Date().getFullYear().toString();
    this.appService.languages$.pipe(untilDestroyed(this)).subscribe(x => {
      this.countries = x;
      this.selectedLang = this.countries.find(y => y.code.toLowerCase() == this.appService.languageCode.toLowerCase());
    });
    const result: any = FooterComponent.footerCacheData ?? (await firstValueFrom(this.resourceApi.getFooter()));
    if (result?.data) {
      FooterComponent.footerCacheData = result;
      const footerData = result.data.footer[0];
      this.licensePic = result.data.license;
      this.disclaimer = this.localeService.brandNameReplace(footerData?.disclaimer?.disclaimer ?? '');
      // footerData.info.map((item: any, index: number) => {
      //   if (item.footerType == this.footerList[index]?.type) {
      //     this.footerList[index].detail = item.detail;
      //   }
      // })
      this.licensePic.forEach(x => {
        if (x.licenseType == 'Code') {
          x.resourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(x.image);
        }
      });
      footerData?.info?.forEach((a: any) => {
        this.footerList.forEach((b: any) => {
          if (a.footerType === b.type) {
            b.detail = a.detail;
          }
        });
      });
    }
  }

  selectLang() {
    this.appService.openLazyComponentDialog('SelectLangDialogComponent');
  }

  /**
   * 获取当前选中国家的语言图标
   *
   * @returns
   */
  countryClassName() {
    if (!this.selectedLang) return '';
    return this.countryUtils.getcountryClassName(this.selectedLang?.code);
  }

  onSelected(i: number) {
    if (this.h5SelectedIndex === i) {
      this.h5SelectedIndex = -1;
      return;
    }
    this.h5SelectedIndex = i;
  }

  getIsLink(url: string) {
    return url.slice(0, 4) === 'http';
  }

  jumpToPage(page: string, isBlank: boolean) {
    if (page == '/online-service') {
      this.appService.toOnLineService$.next(true);
      return;
    }
    // 弹窗网址列判断
    if (page.includes('#')) {
      const popupUrl = page.replace('#', '');
      this.router.navigate([], { relativeTo: this.route, fragment: popupUrl });
      return;
    }
    if (!page) {
      this.toast.show({ message: `${this.localeService.getValue('page_exist')}!`, type: 'fail' });
      return;
    }
    let isCode: boolean = false;
    this.countries
      .map(v => v.code)
      .forEach(e => {
        page
          .split('/')
          .filter(a => a)
          .forEach((b: any) => {
            if (e === b) {
              isCode = true;
            }
          });
      });

    const url = page
      .split('/')
      .slice(isCode ? 2 : 1)
      .join('/');

    if (isBlank) {
      window.open(`${location.origin}/${this.appService.languageCode}/` + url, '_blank');
    } else {
      this.router.navigateByUrl(`/${this.appService.languageCode}/` + url);
    }
  }
  jumpToHome() {
    this.router.navigate(['/', this.appService.languageCode]);
  }

  jumpToGCB() {
    const token =
      'ZXlKcGRpSTZJakJ3Y1VOVGNFeFZTMGQ2WkV4UlduTk5hVXhMUVVFOVBTSXNJblpoYkhWbElqb2ljVlZNSzBsVFRWWkxka0Y0Y0daMGVUaFRaalJFVVQwOUlpd2liV0ZqSWpvaVkyTmtPRGxtWkdJeVpHSmhOems1WVdVMVpXTmhNbUl6WVRNM1pHTmhNakE0T0Roa1ptUTVaVEUyTVdNM1ltUXhaVEk1WVRreE5tSmtNMlZoTm1ObE55SXNJblJoWnlJNklpSjk=';
    window.open(`https://cert.gcb.cw/certificate?id=${token}`, '_blank');
  }
  /**
   * 检查是否为主域名
   *
   * @returns {*}
   * @memberof FooterComponent
   */
  checkMainDomain() {
    const domain = window.location.host;
    // if (domain == 'jackpot88.io' || domain == 'jp88.io') {
    //   return true;
    // }
    return true;
  }

  /**
   * 初始化 ANJ 配置
   */
  initAnjConfig() {
    const host = window.location.hostname;

    // 判斷當前網域，匹配不到則可預設一組
    if (host.includes('jackpot88.io')) {
      this.currentConfig = this.configs['jackpot88.io'];
    } else if (host.includes('jp88.io')) {
      this.currentConfig = this.configs['jp88.io'];
    }

    if (this.currentConfig) {
      this.loadScriptAndInit();
    }
  }

  /**
   * 動態載入 ANJ 腳本並初始化
   */
  private loadScriptAndInit() {
    const scriptSrc = `https://${this.currentConfig.id}.snippet.anjcdn.org/anj-seal.js`;

    // 1. 動態建立 script 標籤
    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.src = scriptSrc;

    // 2. 腳本載入完成後的動作
    script.onload = () => {
      // 延遲執行以確保 DOM 已渲染完畢
      setTimeout(() => {
        const initFnName = this.currentConfig.initFn;
        if (window[initFnName] && window[initFnName].init) {
          window[initFnName].init();
        }
      }, 100);
    };

    this.renderer.appendChild(this.document.head, script);
  }

  initSeal() {
    if (this.currentConfig) {
      const initFnName = this.currentConfig.initFn;
      if (window[initFnName] && window[initFnName].init) {
        window[initFnName].init();
      }
    }
  }

  ngAfterViewInit() {
    // 使用 setTimeout 確保執行緒排在渲染之後
    setTimeout(() => {
      // 初始化 ANJ 配置
      this.initAnjConfig();
      this.initSeal();
    }, 0);
  }
}
